import type { ReactNode } from "react";

import { useState } from "react";

import type { MarketingBannerRow } from "../types";

import { SkeletonLoader } from "../../SkeletonLoader";
import { scaleDimensions } from "./helpers";
import { HTMLAdPreview } from "./HTMLAdPreview";
import { ImageBannerPreview } from "./ImageBannerPreview";
import { UnknownAssetPreview } from "./UnknownAssetPreview";

type BaseProps = {
  banner: MarketingBannerRow;
  frameless?: boolean;
  /** Locale-aware label for the unknown-asset fallback (defaults to English). */
  unknownAssetLabel?: string;
};

type PropsFullSize = BaseProps & {
  maxWidth?: never;
  maxHeight?: never;
};

type PropsScaled = BaseProps & {
  maxWidth: number;
  maxHeight: number;
};

type Props = PropsFullSize | PropsScaled;

/** Inner padding of the framed preview (matches Figma cell spec: 4px). */
const FRAME_PADDING_PX = 4;

export const CreativePreview = ({
  banner,
  maxWidth,
  maxHeight,
  frameless = false,
  unknownAssetLabel,
}: Props) => {
  const [isLoading, setIsLoading] = useState(true);

  const hideLoadingSkeleton = () => setIsLoading(false);

  const originalDimensions = {
    width: banner.width,
    height: banner.height,
  };

  const frameWidth = maxWidth ?? originalDimensions.width;
  const frameHeight = maxHeight ?? originalDimensions.height;

  // In framed mode, reserve 4px padding on each side for visual gutter.
  // In frameless mode, content can use the full max area.
  const padding = frameless ? 0 : FRAME_PADDING_PX;
  const contentMaxDimensions = {
    width: Math.max(frameWidth - padding * 2, 1),
    height: Math.max(frameHeight - padding * 2, 1),
  };

  const { width, height, scale } = scaleDimensions(
    originalDimensions,
    contentMaxDimensions,
  );

  // In frameless mode, the outer box collapses to the actual scaled content
  // size so it doesn't reserve empty space inside a parent container.
  const outerWidth = frameless ? width : frameWidth;
  const outerHeight = frameless ? height : frameHeight;

  const renderPreview = (): ReactNode => {
    switch (banner.type) {
      case "html_ad":
        return (
          <HTMLAdPreview
            banner={banner}
            width={width}
            height={height}
            scale={scale}
            onLoad={hideLoadingSkeleton}
          />
        );
      case "image_banner":
        return (
          <ImageBannerPreview
            banner={banner}
            width={width}
            height={height}
            onLoad={hideLoadingSkeleton}
          />
        );
      default:
        return null;
    }
  };

  const preview = renderPreview();

  const outerClassName = frameless
    ? "relative shrink-0 overflow-hidden"
    : "relative shrink-0 overflow-hidden rounded bg-surface-secondary p-1";

  if (!preview) {
    return (
      <div
        className={`${outerClassName} flex items-center justify-center`}
        style={{ width: outerWidth, height: outerHeight }}
        aria-label={banner.name}
      >
        <UnknownAssetPreview label={unknownAssetLabel} />
      </div>
    );
  }

  return (
    <div
      aria-label={banner.name}
      className={outerClassName}
      style={{ width: outerWidth, height: outerHeight }}
    >
      {isLoading && (
        <div
          className={`flex items-center justify-center ${frameless ? "absolute inset-0" : "absolute inset-1"}`}
        >
          <SkeletonLoader width={width} height={height} />
        </div>
      )}
      <div className="flex h-full w-full items-center justify-center">
        {preview}
      </div>
    </div>
  );
};
